/**
 * Responsibility: turn a town's civic buildings and streets into line segments.
 *
 * The houses are not here. A house is a pitched-roof box, which `structureGeometry.js` has emitted since
 * farmland's barns — so suburbia's common case needed no geometry at all, and this file holds only the handful
 * of things a town has that a farm does not.
 *
 * ## Why these four, and not more
 *
 * Silhouette is the only thing that reads at chase distance, so each of these earns its place by having one:
 *
 * - **Church** — a tapered spire, the only one in the world outside the rock formations. It is what separates a
 *   town from a housing estate.
 * - **Water tower** — a tank standing on legs. Nothing else in the world is a mass held up by open frame.
 * - **School** — a long flat-roofed range, where every other roof in a town is a small triangle.
 * - **Playing field** — flat lines on the ground. It carries no silhouette at all, which is exactly why it is
 *   here: it is what tells you the long building beside it is a school rather than a warehouse.
 *
 * ## Segments in the shape the rest of the world already uses
 *
 * Every function returns `{x1,y1,z1,x2,y2,z2,t1,t2}` records, which is what `emitShapeSegments` consumes for
 * buildings, bridges and airfields. So a town needed no new emitter.
 *
 * `t` is a height fraction over each building's own extent rather than over the world's, for the same reason the
 * airfield does it that way: a street and a spire share a structure kind, and a fraction measured against the
 * tallest thing in a town would leave the streets in the darkest end of the gradient where they are least
 * legible.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { SUBURB } from '../config.js';

/** A segment record in the shape `emitShapeSegments` consumes. */
function line([x1, y1, z1], [x2, y2, z2], t1, t2) {
  return { x1, y1, z1, x2, y2, z2, t1, t2: t2 ?? t1 };
}

/**
 * A frame for placing points relative to a building's centre and facing.
 *
 * @param {{x: number, z: number, facing: number}} placement
 * @returns {(along: number, across: number, y: number) => [number, number, number]}
 */
function frameOf({ x, z, facing }) {
  const alongX = Math.cos(facing);
  const alongZ = Math.sin(facing);

  return (along, across, y) => [
    x + alongX * along - alongZ * across,
    y,
    z + alongZ * along + alongX * across,
  ];
}

/** The four corners of a rectangle in a frame, in order, ready to be closed into a loop. */
function corners(at, halfAlong, halfAcross, y) {
  return [
    at(-halfAlong, -halfAcross, y),
    at(halfAlong, -halfAcross, y),
    at(halfAlong, halfAcross, y),
    at(-halfAlong, halfAcross, y),
  ];
}

/** A closed loop through four points, all at one height fraction. */
function loop(points, t) {
  return points.map((point, i) => line(point, points[(i + 1) % points.length], t));
}

/**
 * A flat-roofed box: two rectangles and four uprights.
 *
 * Local rather than reusing `emitBox`, because that writes straight to a geometry writer while everything here
 * returns segments so the whole building can be assembled and counted before anything is emitted.
 */
function boxSegments(placement, halfAlong, halfAcross, baseY, height, fraction) {
  const at = frameOf(placement);
  const top = baseY + height;

  const low = corners(at, halfAlong, halfAcross, baseY);
  const high = corners(at, halfAlong, halfAcross, top);

  const segments = [...loop(low, fraction(baseY)), ...loop(high, fraction(top))];
  for (let i = 0; i < 4; i += 1) {
    segments.push(line(low[i], high[i], fraction(baseY), fraction(top)));
  }

  return segments;
}

/**
 * A pitched roof over a rectangle: the ridge, and a rafter to each corner.
 *
 * @param {number} ridgeY - Height of the ridge.
 * @param {number} eavesY - Height of the walls it sits on.
 */
function roofSegments(placement, halfAlong, halfAcross, eavesY, ridgeY, fraction) {
  const at = frameOf(placement);
  const eaves = corners(at, halfAlong, halfAcross, eavesY);

  // The ridge runs along the long axis, which is what makes the gable ends the short ones.
  const ridgeA = at(-halfAlong, 0, ridgeY);
  const ridgeB = at(halfAlong, 0, ridgeY);

  const tEaves = fraction(eavesY);
  const tRidge = fraction(ridgeY);

  return [
    line(ridgeA, ridgeB, tRidge),
    line(eaves[0], ridgeA, tEaves, tRidge),
    line(eaves[3], ridgeA, tEaves, tRidge),
    line(eaves[1], ridgeB, tEaves, tRidge),
    line(eaves[2], ridgeB, tEaves, tRidge),
  ];
}

/**
 * A church: a pitched nave with a square tower at one end, capped by a spire.
 *
 * **The spire is the whole reason this building exists.** A town of small triangular roofs needs one thing that
 * is unmistakably not a house, and a taper rising well above the roofline is the cheapest possible way to say so.
 *
 * @param {{x: number, z: number, facing: number, groundY: number}} church
 * @returns {Array<object>} Segment records.
 */
export function churchSegments(church) {
  const at = frameOf(church);

  const naveHalf = SUBURB.CHURCH_NAVE_LENGTH / 2;
  const naveAcross = SUBURB.CHURCH_NAVE_WIDTH / 2;
  const towerHalf = SUBURB.CHURCH_TOWER_WIDTH / 2;

  const eavesY = church.groundY + SUBURB.CHURCH_WALL_HEIGHT;
  const ridgeY = eavesY + SUBURB.CHURCH_ROOF_HEIGHT;
  const towerTop = church.groundY + SUBURB.CHURCH_TOWER_HEIGHT;
  const spireTop = towerTop + SUBURB.CHURCH_SPIRE_HEIGHT;

  /** Height fraction over the church's own extent, so the spire is the brightest thing on it. */
  const span = Math.max(1, spireTop - church.groundY);
  const fraction = (y) => Math.min(1, Math.max(0, (y - church.groundY) / span));

  // Tower at one end of the nave, standing just clear of it so the two read as separate masses.
  const towerAlong = -naveHalf - towerHalf;
  const tower = { x: at(towerAlong, 0, 0)[0], z: at(towerAlong, 0, 0)[2], facing: church.facing };

  const segments = [
    ...boxSegments(church, naveHalf, naveAcross, church.groundY, SUBURB.CHURCH_WALL_HEIGHT, fraction),
    ...roofSegments(church, naveHalf, naveAcross, eavesY, ridgeY, fraction),
    ...boxSegments(tower, towerHalf, towerHalf, church.groundY, SUBURB.CHURCH_TOWER_HEIGHT, fraction),
  ];

  /*
    Spire: four edges from the tower's top corners to a point. A pyramid rather than a cone, because the tower
    beneath it is square and a round spire on a square tower reads as a mistake rather than as a style.
  */
  const towerAt = frameOf(tower);
  const apex = towerAt(0, 0, spireTop);

  for (const [along, across] of [
    [-towerHalf, -towerHalf],
    [towerHalf, -towerHalf],
    [towerHalf, towerHalf],
    [-towerHalf, towerHalf],
  ]) {
    segments.push(line(towerAt(along, across, towerTop), apex, fraction(towerTop), 1));
  }

  return segments;
}

/**
 * A school: a long flat-roofed range with a shorter wing across one end.
 *
 * Flat-roofed on purpose. Every other roof in a town is a small triangle, so a long horizontal one is the
 * contrast — and it is why the building needs no height to stand out.
 *
 * @param {{x: number, z: number, facing: number, groundY: number}} school
 */
export function schoolSegments(school) {
  const at = frameOf(school);

  const mainHalf = SUBURB.SCHOOL_LENGTH / 2;
  const mainAcross = SUBURB.SCHOOL_WIDTH / 2;
  const wingHalf = SUBURB.SCHOOL_WING_LENGTH / 2;
  const wingAcross = SUBURB.SCHOOL_WING_WIDTH / 2;

  const top = school.groundY + SUBURB.SCHOOL_HEIGHT;
  const span = Math.max(1, top - school.groundY);
  const fraction = (y) => Math.min(1, Math.max(0, (y - school.groundY) / span));

  /*
    The wing runs across the main range, so its own frame is rotated a quarter turn. Placed at one end and offset
    outward, which is what makes the pair read as an L rather than as a cross.
  */
  const wingCentre = at(-mainHalf + wingAcross, mainAcross + wingHalf - wingAcross, 0);
  const wing = { x: wingCentre[0], z: wingCentre[2], facing: school.facing + Math.PI / 2 };

  return [
    ...boxSegments(school, mainHalf, mainAcross, school.groundY, SUBURB.SCHOOL_HEIGHT, fraction),
    ...boxSegments(wing, wingHalf, wingAcross, school.groundY, SUBURB.SCHOOL_HEIGHT, fraction),
  ];
}

/**
 * A playing field: a marked rectangle lying on the ground, with a halfway line.
 *
 * **The only thing in a town with no silhouette at all, and that is why it is here.** A long flat-roofed
 * building on its own could be a warehouse; the same building beside a marked pitch could hardly be anything but
 * a school. It costs nothing and it does the explaining.
 *
 * Each edge is drawn in several segments so it follows the ground rather than cutting a chord through it.
 *
 * @param {{x: number, z: number, facing: number}} field
 * @param {(x: number, z: number) => number} sampleGround
 */
export function fieldSegments(field, sampleGround) {
  const at = frameOf(field);
  const halfLength = SUBURB.FIELD_LENGTH / 2;
  const halfWidth = SUBURB.FIELD_WIDTH / 2;
  const steps = SUBURB.FIELD_SEGMENTS;

  /** A point on the ground, lifted clear of it so it is never buried by the terrain lattice. */
  const onGround = (along, across) => {
    const [x, , z] = at(along, across, 0);
    return [x, sampleGround(x, z) + SUBURB.STREET_LIFT, z];
  };

  const segments = [];

  /** One edge, drawn in `steps` pieces between two corners of the rectangle. */
  const edge = (fromAlong, fromAcross, toAlong, toAcross) => {
    for (let i = 0; i < steps; i += 1) {
      const a = i / steps;
      const b = (i + 1) / steps;
      segments.push(
        line(
          onGround(fromAlong + (toAlong - fromAlong) * a, fromAcross + (toAcross - fromAcross) * a),
          onGround(fromAlong + (toAlong - fromAlong) * b, fromAcross + (toAcross - fromAcross) * b),
          0,
        ),
      );
    }
  };

  edge(-halfLength, -halfWidth, halfLength, -halfWidth);
  edge(halfLength, -halfWidth, halfLength, halfWidth);
  edge(halfLength, halfWidth, -halfLength, halfWidth);
  edge(-halfLength, halfWidth, -halfLength, -halfWidth);

  // The halfway line, which is what makes it a pitch rather than a fenced paddock.
  edge(0, -halfWidth, 0, halfWidth);

  return segments;
}

/**
 * A water tower: an octagonal tank standing on splayed legs.
 *
 * Nothing else in the world is a solid mass held up by an open frame, so this reads as itself from a long way
 * off — which is the job, since it is one of only two things in a town that clear the roofline.
 *
 * @param {{x: number, z: number, facing: number, groundY: number}} tower
 */
export function waterTowerSegments(tower) {
  const at = frameOf(tower);

  const legTop = tower.groundY + SUBURB.TOWER_LEG_HEIGHT;
  const tankTop = legTop + SUBURB.TOWER_TANK_HEIGHT;
  const radius = SUBURB.TOWER_TANK_RADIUS;
  const sides = SUBURB.TOWER_TANK_SIDES;

  const span = Math.max(1, tankTop - tower.groundY);
  const fraction = (y) => Math.min(1, Math.max(0, (y - tower.groundY) / span));

  /** A point on the tank's polygon at a given height. */
  const rim = (i, y) => {
    const angle = (i / sides) * Math.PI * 2;
    return at(Math.cos(angle) * radius, Math.sin(angle) * radius, y);
  };

  const segments = [];

  // Tank: a ring top and bottom, and an upright at every corner between them.
  for (let i = 0; i < sides; i += 1) {
    segments.push(line(rim(i, legTop), rim((i + 1) % sides, legTop), fraction(legTop)));
    segments.push(line(rim(i, tankTop), rim((i + 1) % sides, tankTop), 1));
    segments.push(line(rim(i, legTop), rim(i, tankTop), fraction(legTop), 1));
  }

  /*
    Legs splayed outward at the base, which is both what a real tower has and what stops the whole thing reading
    as a single column. Four of them, taken from the tank's alternate corners so they line up with it.
  */
  const spread = SUBURB.TOWER_LEG_SPREAD;

  for (let i = 0; i < 4; i += 1) {
    const angle = (i / 4) * Math.PI * 2;
    const footX = Math.cos(angle) * (radius + spread);
    const footZ = Math.sin(angle) * (radius + spread);
    const topX = Math.cos(angle) * radius;
    const topZ = Math.sin(angle) * radius;

    segments.push(
      line(
        at(footX, footZ, tower.groundY),
        at(topX, topZ, legTop),
        0,
        fraction(legTop),
      ),
    );
  }

  return segments;
}

/**
 * A street: a line along one edge of a block, draped over the ground.
 *
 * **Worth drawing, unlike the city's streets, which are not drawn at all.** A downtown explains itself through
 * the gaps between tall towers; a town's buildings are small and low, so from altitude the arrangement is the
 * only signal, and without lines along the block edges a town reads as a field of unrelated dots.
 *
 * @param {[number, number]} from - World start.
 * @param {[number, number]} to - World end.
 * @param {(x: number, z: number) => number} sampleGround
 */
export function streetSegments(from, to, sampleGround) {
  const steps = SUBURB.STREET_SEGMENTS;
  const segments = [];

  const point = (t) => {
    const x = from[0] + (to[0] - from[0]) * t;
    const z = from[1] + (to[1] - from[1]) * t;
    return [x, sampleGround(x, z) + SUBURB.STREET_LIFT, z];
  };

  for (let i = 0; i < steps; i += 1) {
    segments.push(line(point(i / steps), point((i + 1) / steps), 0));
  }

  return segments;
}
